import React, { useCallback, useEffect, useRef, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Receipt, Upload } from "lucide-react";

const ST = {
  submitted: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-sky-50 text-sky-700 border-sky-200",
  reimbursed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  booked: "bg-slate-50 text-slate-600 border-slate-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};
const chf = (v) => Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");

// Iter313: Mitarbeiterportal – Beleg einreichen + Meine Spesen
export function PortalReceiptsCard() {
  const [purposes, setPurposes] = useState([]);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ purpose: "", note: "", paid_by_employee: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [policy, setPolicy] = useState(null);
  const [lump, setLump] = useState({ lump_type: "km", quantity: "", date: new Date().toISOString().slice(0, 10), route: "", note: "" });
  const [lumps, setLumps] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, r, pol, lc] = await Promise.all([apiClient.get("/my/portal/purposes"), apiClient.get("/my/portal/receipts"),
        apiClient.get("/my/portal/expense-policy"), apiClient.get("/my/portal/expense-claims")]);
      setPurposes(p.data || []);
      setData(r.data);
      setPolicy(pol.data);
      setLumps((lc.data?.items || []).filter((c) => c.kind === "lump_sum"));
    } catch { /* kein Portal-Zugang */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) { toast.error("Bitte ein Foto oder PDF des Belegs wählen."); return; }
    if (!form.purpose) { toast.error("Bitte angeben, wofür der Beleg ist."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", form.purpose);
      if (form.note) fd.append("note", form.note);
      fd.append("paid_by_employee", form.paid_by_employee ? "true" : "false");
      const r = await apiClient.post("/my/portal/receipts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.expense_claim
        ? "Beleg eingereicht – nach Prüfung wird der Betrag mit dem Lohn zurückerstattet"
        : "Beleg eingereicht – wird von der Buchhaltung geprüft");
      setForm({ purpose: "", note: "", paid_by_employee: true });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Einreichen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const lumpAmount = () => {
    if (!policy || !lump.quantity) return 0;
    const q = Number(lump.quantity);
    if (lump.lump_type === "km") return Math.round(policy.km_rate * q * 20) / 20;
    return (lump.lump_type === "lunch" ? policy.meal_lunch : policy.meal_dinner) * q;
  };

  const submitLump = async () => {
    if (!lump.quantity || Number(lump.quantity) <= 0) { toast.error(lump.lump_type === "km" ? "Bitte Kilometer angeben." : "Bitte Anzahl angeben."); return; }
    setBusy(true);
    try {
      await apiClient.post("/my/portal/expense-lump-sums", { ...lump, quantity: Number(lump.quantity), route: lump.route || null, note: lump.note || null });
      toast.success(`Pauschale CHF ${chf(lumpAmount())} eingereicht – wartet auf Freigabe`);
      setLump({ ...lump, quantity: "", route: "", note: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Einreichen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="portal-lump-sum-card">
        <h2 className="text-sm font-semibold mb-1">Pauschale erfassen (ohne Beleg)</h2>
        {policy && <p className="text-xs text-slate-500 mb-3" data-testid="portal-lump-sum-policy">Spesenreglement: CHF {Number(policy.km_rate).toFixed(2)}/km · Mittagessen CHF {Number(policy.meal_lunch).toFixed(2)} · Abendessen CHF {Number(policy.meal_dinner).toFixed(2)}</p>}
        <div className="grid grid-cols-3 gap-2 items-center">
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white" value={lump.lump_type} onChange={(e) => setLump({ ...lump, lump_type: e.target.value })} data-testid="portal-lump-type-select">
            <option value="km">Kilometer (Privatauto)</option>
            <option value="lunch">Mittagessen</option>
            <option value="dinner">Abendessen</option>
          </select>
          <Input type="number" min="0" step={lump.lump_type === "km" ? "1" : "1"} placeholder={lump.lump_type === "km" ? "km" : "Anzahl"} value={lump.quantity} onChange={(e) => setLump({ ...lump, quantity: e.target.value })} data-testid="portal-lump-quantity-input" />
          <SwissDateField  value={lump.date} onChange={(e) => setLump({ ...lump, date: e.target.value })} data-testid="portal-lump-date-input" />
        </div>
        <div className="mt-2 flex gap-2 items-center">
          <Input placeholder={lump.lump_type === "km" ? "Strecke, z.B. Wetzikon–Zürich" : "Anlass / Notiz"} value={lump.lump_type === "km" ? lump.route : lump.note} onChange={(e) => setLump(lump.lump_type === "km" ? { ...lump, route: e.target.value } : { ...lump, note: e.target.value })} data-testid="portal-lump-note-input" />
          <Button disabled={busy} onClick={submitLump} className="bg-slate-900 text-white whitespace-nowrap shrink-0" data-testid="portal-lump-submit-btn">
            {busy ? "…" : `Einreichen · CHF ${chf(lumpAmount())}`}
          </Button>
        </div>
        {lumps.length > 0 && (
          <div className="mt-3 divide-y divide-slate-100">
            {lumps.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 py-1.5 text-sm" data-testid={`portal-lump-row-${c.id}`}>
                <span className="truncate text-slate-700">{formatDate(c.date)} · {c.supplier_name} · CHF {chf(c.amount)}</span>
                <span className={`text-[11px] border rounded-full px-2 py-0.5 whitespace-nowrap ${ST[c.status] || ST.booked}`} data-testid={`portal-lump-status-${c.id}`}>{c.status_label}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="portal-receipt-upload-card">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-3"><Receipt className="h-4 w-4" /> Beleg einreichen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="portal-receipt-file-input" />
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="portal-receipt-purpose-select">
            <option value="">Wofür? (Pflicht)</option>
            {purposes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Notiz (optional), z.B. Team-Lunch mit Kollegen" data-testid="portal-receipt-note-input" />
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="portal-receipt-paid-label">
            <input type="checkbox" checked={form.paid_by_employee} onChange={(e) => setForm({ ...form, paid_by_employee: e.target.checked })} data-testid="portal-receipt-paid-checkbox" />
            Ich habe privat bezahlt (Rückerstattung)
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {form.paid_by_employee
              ? (data?.mode === "bank" ? "Rückerstattung erfolgt per Banküberweisung nach Prüfung." : "Rückerstattung erfolgt mit dem nächsten Lohn (AHV-/steuerfrei).")
              : "Firmenbeleg – wurde mit Firmenkarte/-kasse bezahlt."}
          </p>
          <Button disabled={busy} onClick={submit} className="bg-slate-900 text-white" data-testid="portal-receipt-submit-btn">
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Sende…" : "Einreichen"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="portal-expenses-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Meine Spesen & Belege</h2>
          {data && (
            <span className="text-xs text-slate-600" data-testid="portal-expenses-summary">
              offen <b>CHF {chf(data.open_reimbursement)}</b> · erstattet CHF {chf(data.reimbursed_total)}
            </span>
          )}
        </div>
        {!data?.items?.length && <p className="text-sm text-slate-400" data-testid="portal-expenses-empty">Noch keine Belege eingereicht.</p>}
        {data?.items?.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-sm" data-testid={`portal-receipt-row-${it.id}`}>
            <div className="min-w-0">
              <div className="text-slate-800 truncate">
                {it.supplier_name || it.file_name || "Beleg"}{it.amount != null ? ` · CHF ${chf(it.amount)}` : ""}
              </div>
              <div className="text-[11px] text-slate-500">
                {formatDate(it.created_at?.slice(0, 10))}{it.purpose_label ? ` · ${it.purpose_label}` : ""}{it.expense_claim ? " · privat bezahlt" : ""}
                {it.detail ? ` · ${it.detail}` : ""}
              </div>
            </div>
            <span className={`text-[11px] border rounded-full px-2 py-0.5 whitespace-nowrap ${ST[it.status] || ST.booked}`} data-testid={`portal-receipt-status-${it.id}`}>{it.label}</span>
          </div>
        ))}
      </Card>
    </>
  );
}
